/**
 * @fileoverview Consent Service — Manages granular cookie/storage consent preferences.
 *
 * Provides read/write/migrate/clear helpers for the browser-side consent state.
 * Consent is persisted in both localStorage and a first-party cookie.
 *
 * Policy version: Bump CONSENT_POLICY_VERSION whenever category purposes or
 * storage mappings change so that users are re-prompted when legally required.
 *
 * Key format:
 * - localStorage: {namespace}-consent-v1
 * - cookie: {namespace}-consent
 *
 * @module lib/ConsentService
 */

import type {
  ConsentCategory,
  ConsentPreferences,
  ConsentSource,
  ConsentState,
} from '@xanectech/types';

export const CONSENT_STORAGE_KEY = 'xanec-consent-v1';
export const CONSENT_COOKIE_KEY = 'xanec-consent';
export const CONSENT_COOKIE_MAX_AGE_SECONDS = 31536000; // 1 year

const APP_CONSENT_NAMESPACE_MAP: Record<string, string> = {
  home: 'xanec',
  'trueid-locker': 'trueid-locker',
  admin: 'xanec-admin',
  company: 'xanec-company',
  xanectech: 'xanec',
};

/**
 * Current policy version. Bump this when consent categories, purposes, or
 * storage mappings change in a legally material way.
 */
export const CONSENT_POLICY_VERSION = 1;

/**
 * Default (essential-only) preferences used as a fallback when consent data
 * is missing or malformed.
 */
export const ESSENTIAL_ONLY_PREFERENCES: ConsentPreferences = {
  essential: true,
  functional: false,
  analytics: false,
};

function getConsentNamespace(appName?: string): string {
  const normalized = (appName ?? 'xanectech').toLowerCase();
  return APP_CONSENT_NAMESPACE_MAP[normalized] ?? normalized;
}

function getConsentStorageKey(appName?: string): string {
  if (!appName) return CONSENT_STORAGE_KEY;
  return `${getConsentNamespace(appName)}-consent-v1`;
}

function getConsentCookieKey(appName?: string): string {
  if (!appName) return CONSENT_COOKIE_KEY;
  return `${getConsentNamespace(appName)}-consent`;
}

function parseConsentState(raw: string | null): ConsentState | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as ConsentState;

    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof parsed.version !== 'number' ||
      typeof parsed.preferences !== 'object'
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function readConsentFromCookie(appName?: string): ConsentState | null {
  if (typeof document === 'undefined') return null;

  const cookiePrefix = `${getConsentCookieKey(appName)}=`;
  const rawCookie = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(cookiePrefix));

  if (!rawCookie) return null;

  const encodedValue = rawCookie.slice(cookiePrefix.length);
  const decodedValue = decodeURIComponent(encodedValue);
  return parseConsentState(decodedValue);
}

function writeConsentCookie(state: ConsentState, appName?: string): void {
  if (typeof document === 'undefined') return;

  const encodedState = encodeURIComponent(JSON.stringify(state));
  const isHttps =
    typeof location !== 'undefined' && location.protocol === 'https:';
  const secure = isHttps ? '; Secure' : '';
  const cookieKey = getConsentCookieKey(appName);

  document.cookie = `${cookieKey}=${encodedState}; Max-Age=${CONSENT_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

function clearConsentCookie(appName?: string): void {
  if (typeof document === 'undefined') return;
  const cookieKey = getConsentCookieKey(appName);
  document.cookie = `${cookieKey}=; Max-Age=0; Path=/; SameSite=Lax`;
}

/**
 * Read the stored consent state from browser storage.
 *
 * Lookup order:
 * 1. localStorage (primary)
 * 2. consent cookie (fallback)
 *
 * If the cookie fallback is used, localStorage is backfilled.
 * Returns null if no decision has been made yet or data is malformed.
 */
export function readConsent(appName?: string): ConsentState | null {
  let localState: ConsentState | null = null;
  const storageKey = getConsentStorageKey(appName);

  if (typeof localStorage !== 'undefined') {
    try {
      localState = parseConsentState(localStorage.getItem(storageKey));
      if (localState) return localState;
    } catch {
      // Fall through to cookie lookup.
    }
  }

  const cookieState = readConsentFromCookie(appName);

  if (cookieState && typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(storageKey, JSON.stringify(cookieState));
    } catch {
      // Ignore localStorage write errors; cookie remains source of truth.
    }
  }

  return cookieState;
}

/**
 * Persist a consent decision to localStorage and a first-party cookie.
 *
 * @param preferences - Functional and analytics consent flags (essential is always true).
 * @param source - The action that triggered this save.
 * @returns The persisted ConsentState.
 */
export function writeConsent(
  preferences: Pick<ConsentPreferences, 'functional' | 'analytics'>,
  source: ConsentSource,
  appName?: string,
): ConsentState {
  const state: ConsentState = {
    version: CONSENT_POLICY_VERSION,
    preferences: { essential: true, ...preferences },
    source,
    decidedAt: new Date().toISOString(),
  };
  const storageKey = getConsentStorageKey(appName);

  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(storageKey, JSON.stringify(state));
  }
  writeConsentCookie(state, appName);
  return state;
}

/**
 * Remove consent data from localStorage and cookie, effectively resetting the user to
 * the "not decided" state (banner will re-appear).
 */
export function clearConsent(appName?: string): void {
  const storageKey = getConsentStorageKey(appName);

  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(storageKey);
  }
  clearConsentCookie(appName);
}

/**
 * Check whether a specific consent category is currently granted.
 *
 * Essential is always granted. All other categories require an explicit opt-in.
 * Falls back to essential-only if no decision has been recorded.
 *
 * @param category - The consent category to check.
 */
export function checkConsentCategory(
  category: ConsentCategory,
  appName?: string,
): boolean {
  if (category === 'essential') return true;
  const state = readConsent(appName);
  if (!state) return false; // opt-in default: block non-essential when undecided
  return state.preferences[category] ?? false;
}

/**
 * Returns true if the user has already made a consent decision (any source).
 */
export function hasConsentDecision(appName?: string): boolean {
  return readConsent(appName) !== null;
}

/**
 * Attempt to migrate a legacy binary consent value (from react-cookie) into
 * the new structured format.
 *
 * Legacy schema:
 *   - Cookie `user-consent-{appname}` = 'accepted' → all categories enabled
 *   - Cookie `user-consent` = 'essential'           → essential-only
 *
 * Call this once during app bootstrap if `hasConsentDecision()` is false.
 *
 * @param legacyValue - Value read from the legacy consent cookie ('accepted' | 'essential' | null).
 * @returns The migrated ConsentState, or null if no legacy value is present.
 */
export function migrateLegacyConsent(
  legacyValue: string | null | undefined,
  appName?: string,
): ConsentState | null {
  if (!legacyValue) return null;

  if (legacyValue === 'accepted') {
    return writeConsent(
      { functional: true, analytics: true },
      'migrated',
      appName,
    );
  }

  if (legacyValue === 'essential') {
    return writeConsent(
      { functional: false, analytics: false },
      'migrated',
      appName,
    );
  }

  return null;
}
